import { NavLink } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";
import { useT } from "@/i18n/I18nContext";
import { AequiaMark, Icon, Tooltip } from "@/components/ui";
import { WorkspaceSwitcher } from "@/components/layout/WorkspaceSwitcher";
import {
  canAccessMenuRouteHandle,
  PROTECTED_ROUTES,
  type RouteSpec,
} from "@/config/routes";
import type { RbacLevel } from "@/config/rbac";

interface SidebarProps {
  onCloseDrawer?: () => void;
  mobile?: boolean;
}

interface NavSection {
  labelKey: string;
  items: RouteSpec[];
  subsections: NavSubsection[];
}

interface NavSubsection {
  labelKey: string;
  items: RouteSpec[];
}

interface NavSectionConfig {
  labelKey: string;
  paths?: readonly string[];
  subsections?: readonly NavSubsectionConfig[];
}

interface NavSubsectionConfig {
  labelKey: string;
  paths: readonly string[];
}

type NavAccess = {
  hasPermission: (code: string) => boolean;
  hasAnyPermission: (codes: string[]) => boolean;
  hasModuleLevel: (moduleCode: string, level: RbacLevel) => boolean;
  isSuperadmin: boolean;
};

const NAV_SECTIONS: readonly NavSectionConfig[] = [
  {
    labelKey: "nav.section.legal",
    paths: ["/dashboard", "/clients", "/matters", "/counterparties", "/tasks"],
  },
  {
    labelKey: "nav.section.docs",
    paths: ["/documents", "/templates", "/signature"],
  },
  {
    labelKey: "nav.section.flows",
    paths: ["/workflows"],
  },
  {
    labelKey: "nav.section.ai",
    paths: ["/ai", "/rag"],
  },
  {
    labelKey: "nav.section.control",
    paths: ["/dashboards", "/reports", "/risks"],
  },
  {
    labelKey: "nav.section.comms",
    paths: ["/notifications"],
  },
  {
    labelKey: "nav.admin",
    subsections: [
      {
        labelKey: "nav.admin.group.access",
        paths: ["/admin/rbac", "/admin/tenants"],
      },
      {
        labelKey: "nav.admin.group.business",
        paths: [
          "/admin/matter-types",
          "/admin/matter-templates",
          "/admin/risk-categories",
          "/admin/scoring-policies",
        ],
      },
      {
        labelKey: "nav.admin.group.comms",
        paths: [
          "/admin/email-setting",
          "/admin/email-templates",
          "/admin/notifications",
        ],
      },
      {
        labelKey: "nav.admin.group.ai",
        paths: ["/admin/system-config/ai", "/admin/cost", "/admin/stability"],
      },
      {
        labelKey: "nav.admin.group.monitoring",
        paths: [
          "/admin/system-status",
          "/admin/audit",
          "/admin/compliance-requests",
        ],
      },
      {
        labelKey: "nav.admin.group.techconfig",
        paths: ["/admin/system-config", "/admin/architecture"],
      },
    ],
  },
] as const;

function buildSections(access: NavAccess): NavSection[] {
  const routesByPath = new Map(
    PROTECTED_ROUTES.filter(
      (route) => route.handle?.labelKey && !route.handle.hideInNav,
    ).map((route) => [route.path, route]),
  );

  const resolveItems = (paths: readonly string[]) =>
    paths
      .map((path) => routesByPath.get(path))
      .filter((route): route is RouteSpec => Boolean(route))
      .filter((route) => canAccessMenuRouteHandle(route.handle, access));

  return NAV_SECTIONS.map((section) => {
    const items = section.paths ? resolveItems(section.paths) : [];
    const subsections =
      section.subsections
        ?.map((subsection) => ({
          labelKey: subsection.labelKey,
          items: resolveItems(subsection.paths),
        }))
        .filter((subsection) => subsection.items.length > 0) ?? [];
    return {
      labelKey: section.labelKey,
      items,
      subsections,
    };
  }).filter(
    (section) => section.items.length > 0 || section.subsections.length > 0,
  );
}

export function Sidebar({ onCloseDrawer, mobile }: SidebarProps) {
  const { hasPermission, hasAnyPermission, hasModuleLevel, isSuperadmin } =
    useAuth();
  const { sidebarCollapsed, toggleSidebar } = useUI();
  const { t } = useT();
  const collapsed = sidebarCollapsed && !mobile;
  const sections = buildSections({
    hasPermission,
    hasAnyPermission,
    hasModuleLevel,
    isSuperadmin,
  });
  const renderNavItem = (it: RouteSpec) => {
    const label = it.handle?.labelKey ?? it.path;
    const iconName = it.handle?.icon ?? "doc";
    const item = (
      <NavLink
        key={it.path}
        to={it.path}
        end={it.path === "/dashboard"}
        onClick={() => mobile && onCloseDrawer?.()}
        aria-label={collapsed ? t(label) : undefined}
        title={collapsed ? t(label) : undefined}
        className={({ isActive }) =>
          `w-full flex items-center gap-3 ${
            collapsed ? "px-2.5 justify-center" : "px-3"
          } py-2 rounded text-sm transition relative mb-0.5 ${
            isActive
              ? "bg-bg-2 text-text font-semibold"
              : "text-text-2 hover:bg-text/[.04]"
          }`
        }
      >
        {({ isActive }) => (
          <>
            {isActive && (
              <span className="absolute left-[-8px] top-1.5 bottom-1.5 w-[2.5px] rounded bg-primary" />
            )}
            <Icon name={iconName} size={16} />
            {!collapsed && <span className="flex-1 text-left">{t(label)}</span>}
          </>
        )}
      </NavLink>
    );
    return collapsed ? (
      <Tooltip key={it.path} content={t(label)} placement="right" delay={400}>
        {item}
      </Tooltip>
    ) : (
      item
    );
  };

  return (
    <aside
      className={`flex flex-col bg-bg border-r border-border h-screen transition-[width] duration-200 ease-out ${
        mobile ? "fixed top-0 left-0 z-40 shadow-lg" : "sticky top-0"
      }`}
      style={{ width: collapsed ? 60 : 240 }}
      aria-label="Navegación principal"
      data-tour-id="sidebar-collapse-root"
    >
      <div
        className={`${collapsed ? "px-2 justify-center" : "px-3 justify-between"} pt-5 pb-3 flex items-center gap-2`}
      >
        {!collapsed ? (
          <AequiaMark size={68} />
        ) : (
          <AequiaMark variant="short" size={46} />
        )}
        {mobile && (
          <button
            onClick={onCloseDrawer}
            className="p-1 text-muted"
            aria-label="Cerrar menú"
          >
            <Icon name="close" size={18} />
          </button>
        )}
      </div>

      <WorkspaceSwitcher compact={collapsed} />

      <nav className="flex-1 overflow-y-auto px-2 pb-4">
        {sections.map((section, gi) => {
          const visibleItems = section.items;
          const visibleSubsections = section.subsections;
          if (visibleItems.length === 0 && visibleSubsections.length === 0)
            return null;
          return (
            <div key={gi} className="mb-4">
              {!collapsed && (
                <div className="px-3 py-1 text-[10.5px] uppercase tracking-eyebrow text-muted font-semibold">
                  {t(section.labelKey)}
                </div>
              )}
              {visibleItems.map(renderNavItem)}
              {visibleSubsections.map((subsection) => (
                <div
                  key={subsection.labelKey}
                  className={collapsed ? "mb-1" : "mb-2"}
                >
                  {!collapsed && (
                    <div className="px-3 pb-1 pt-2 text-[9.5px] font-semibold uppercase tracking-[0.14em] text-muted/75">
                      {t(subsection.labelKey)}
                    </div>
                  )}
                  {subsection.items.map(renderNavItem)}
                </div>
              ))}
            </div>
          );
        })}
      </nav>

      {!mobile && (
        <div className="p-2.5 border-t border-border">
          <Tooltip
            content={
              collapsed
                ? "Expandir menú (Cmd/Ctrl+B)"
                : "Colapsar menú (Cmd/Ctrl+B)"
            }
            placement="right"
          >
            <button
              type="button"
              onClick={toggleSidebar}
              className={`flex items-center rounded-md border border-border text-xs font-medium text-text-2 transition hover:border-border-strong hover:bg-bg-2 hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45 ${
                collapsed
                  ? "mx-auto h-9 w-9 justify-center"
                  : "w-full justify-between gap-2 px-3 py-2"
              }`}
              aria-label={collapsed ? "Expandir menú" : "Colapsar menú"}
              title="Cmd/Ctrl + B"
              data-tour-id="sidebar-collapse-toggle"
            >
              {!collapsed && <span>Contraer menú</span>}
              <Icon
                name={collapsed ? "panelLeftOpen" : "panelLeftClose"}
                size={16}
              />
            </button>
          </Tooltip>
        </div>
      )}
    </aside>
  );
}
